import { getEmailTemplates, orEmptyArray } from '@lms/api-client/backend';
import { Card, CardContent, CardDescription, CardHeader } from '@lms/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { AddEmailTemplate } from '@/features/email/components/add-email-template';
import { EMAIL_TEMPLATES_KEY } from '@/features/email/email-keys';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/email/page.tsx. The Next server component awaited the
// email-template list; here it loads via SWR. The section is super-admin only
// (the Next header exposed it only inside the super-admin nav group), so a
// non-super-admin gets <NotFound />. Renders content inside the admin Shell.
export function Email() {
  const t = useTranslations();
  const { brand: site, slug, loading } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);

  const { data: items, mutate } = useSWR(EMAIL_TEMPLATES_KEY, () =>
    orEmptyArray(getEmailTemplates(null), [401]),
  );

  if (loading || roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!access?.superAdmin) {
    return <NotFound />;
  }

  if (items === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('admin.email_templates')}</PageTitle>
      </PageHeader>
      <div className="grid items-start gap-8 md:grid-cols-4">
        <Card className="col-span-1 md:col-span-3">
          <CardHeader>
            <CardDescription>
              {t('admin.manage', {
                what: t('admin.email_templates').toLowerCase(),
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.language')}</TableHead>
                  <TableHead>{t('admin.name')}</TableHead>
                  <TableHead>{t('admin.description')}</TableHead>
                  <TableHead>{t('admin.site')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{t(`languages.${p.language}`)}</TableCell>
                    <TableCell>
                      <Link to={`/${slug}/admin/email/${p.id}`}>
                        {t(`email.Templates.${p.key}.name`)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {t(`email.Templates.${p.key}.description`)}
                    </TableCell>
                    <TableCell>{p.siteName ?? 'Global'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <AddEmailTemplate siteId={site.id} onCreated={() => mutate()} />
      </div>
    </PageBox>
  );
}
